import { useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  Input,
  type SegmentedControlOption,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { CompactCivilDateField } from '~/components/common/CompactCivilDateField'
import { DsSelect, type DsSelectOptions } from '~/components/common/DsSelect'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import {
  type CivilDate,
  civilDateToDate,
  dateToCivilDate,
  todayCivilDate,
} from '~/models/civilDate'
import { addDays } from '~/models/dateTime'
import type { VoltageClass } from '~/models/voltageBreakdown'
import {
  FormGrid,
  GridLabel,
  ReadOnlyRow,
} from '~/pages/CustomerInfo/components/FormField'
import { FormModalShell } from '~/pages/CustomerInfo/components/FormModalShell'
import {
  useCreateSupplyPoint,
  useGetFormOptions,
} from '~/pages/CustomerInfo/hooks/useCustomerInfoQueries'
import {
  VOLTAGE_CLASSES,
  VOLTAGE_I18N_KEY,
} from '~/pages/CustomerInfo/models/display'
import {
  isInvalidKwInput,
  parseKwInput,
  resolveSupplyPointError,
} from '~/pages/CustomerInfo/models/resolveSupplyPointError'
import {
  SupplyPointError,
  type SupplyPointErrorReason,
} from '~/pages/CustomerInfo/models/supplyPointError'
import type {
  ContractorSelection,
  CreateSupplyPointInput,
  CustomerSelection,
} from '~/pages/CustomerInfo/models/types'

type Props = {
  /** customer = 需要家の追加 (新しい需要家と最初の契約) / contract = 既存の需要家に次の契約を足す。 */
  mode: 'customer' | 'contract'
  /** 契約推移タブで選択中のエリア (既定値)。 */
  defaultAreaCode: AreaCode
  onClose: () => void
}

type Stage = 'contracted' | 'prospect'
type CustomerKind = 'existing' | 'new'
type ContractorKind = 'existing' | 'new'

/** 小売を決めていない (見込みのみ)。 */
const RETAILER_UNDECIDED = '__undecided__'

/** 供給開始日の既定 = 今日 + 7 日 (モックと同じ)。 */
const defaultSupplyStart = (): CivilDate =>
  dateToCivilDate(addDays(civilDateToDate(todayCivilDate()), 7))

/**
 * 需要家の追加 / 契約の追加モーダル (`mode`)。 項目の並びと文言はモック (`ContractEditModal` の新規) に合わせる:
 * 区分 → 需要家 (既存 / 新規) → 需要家名 → (新規の時) 契約者 → エリア → 小売事業者 → 電圧階級 →
 * 供給地点特定番号 → 契約kW → 供給開始日 → 供給終了日 → 自家発電設備。
 * 検査は server 相当 (`useCreateSupplyPoint`) が行い、 拒否理由を上部の Alert に 1 件出す (入力は保持)。
 */
export const CreateContractModal = ({
  mode,
  defaultAreaCode,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { mutateAsync, isPending } = useCreateSupplyPoint()

  const [isDirty, setIsDirty] = useState(false)
  const [chosenStage, setStage] = useState<Stage>('contracted')
  // 需要家の追加は新しい需要家、 契約の追加は既存の需要家 (切り替えは無い)
  const customerKind: CustomerKind = mode === 'customer' ? 'new' : 'existing'
  const [customerId, setCustomerId] = useState<string | undefined>()
  const [customerName, setCustomerName] = useState('')
  // 契約者の選び方: 触るまでは既定 (契約者が 1 件も無い組織は新規、 他は既存)
  const [chosenContractorKind, setContractorKind] = useState<
    ContractorKind | undefined
  >()
  const [newContractorName, setNewContractorName] = useState('')
  const [contractorId, setContractorId] = useState<string | undefined>()
  const [areaCode, setAreaCode] = useState<AreaCode>(defaultAreaCode)
  const [retailerCode, setRetailerCode] = useState<string | undefined>()
  const [voltage, setVoltage] = useState<VoltageClass>('high')
  const [number, setNumber] = useState('')
  const [kw, setKw] = useState('')
  const [start, setStart] = useState<CivilDate | undefined>(defaultSupplyStart)
  const [end, setEnd] = useState<CivilDate | undefined>()
  const [hasGen, setHasGen] = useState(false)
  const [capacity, setCapacity] = useState('')
  const [hasReverse, setHasReverse] = useState(false)
  const [reverseKw, setReverseKw] = useState('')
  const [error, setError] = useState<string | undefined>()

  const { data: options } = useGetFormOptions(areaCode, true)
  const retailerOptions = options?.retailerOptions ?? []
  const contractorKind: ContractorKind =
    chosenContractorKind ??
    (options !== undefined && options.contractorOptions.length === 0
      ? 'new'
      : 'existing')
  // 新しい契約者を作る時は、 契約者名が入るまで保存できない
  const isContractorNameMissing =
    customerKind === 'new' &&
    contractorKind === 'new' &&
    newContractorName.trim() === ''
  // 需要家 1 件は 1 つのエリアに属する: 既存の需要家を選んだら、 エリアはその需要家のエリアに固定する
  const chosenCustomer =
    customerKind === 'existing' && customerId !== undefined
      ? (options?.customerOptions ?? []).find(
          (c) => c.customerId === customerId,
        )
      : undefined
  const lockedArea = chosenCustomer?.areaCodes[0]
  // 需要家 1 件が持つ番号は 1 つ (R48): 番号のある需要家に足す契約は、 その番号で固定する。
  // 見込み (番号なし) を足せるのは番号の無い需要家だけ
  const lockedNumber =
    mode === 'contract' ? chosenCustomer?.supplyPointNumber : undefined
  const stage: Stage = lockedNumber === undefined ? chosenStage : 'contracted'
  const effectiveArea = lockedArea ?? areaCode
  // 小売はモックどおり常に出し、 先頭を既定にする (見込みは「未定」も選べる)
  const showRetailerSelect = retailerOptions.length > 0
  const effectiveRetailer =
    retailerCode === undefined
      ? retailerOptions[0]?.businessCode
      : retailerCode === RETAILER_UNDECIDED
        ? undefined
        : retailerCode

  const touch = <T,>(setter: (value: T) => void) => {
    return (value: T): void => {
      setIsDirty(true)
      setError(undefined)
      setter(value)
    }
  }

  const stageOptions: Array<SegmentedControlOption<Stage>> = [
    { value: 'contracted', label: t('page:customerInfo.form.stageContracted') },
    {
      value: 'prospect',
      label: t('page:customerInfo.form.stageProspect'),
      disabled: lockedNumber !== undefined,
    },
  ]
  const contractorKindOptions: Array<SegmentedControlOption<ContractorKind>> = [
    {
      value: 'existing',
      label: t('page:customerInfo.form.contractorExisting'),
    },
    {
      value: 'new',
      label: t('page:customerInfo.form.contractorNew'),
    },
  ]
  // 既存の需要家の選択肢: 需要家名 (エリア / 供給地点 N 件)。 同名の需要家を見分ける
  const customerSelectOptions: DsSelectOptions = [
    ...(options?.customerOptions ?? []),
  ]
    .sort((a, b) => a.customerName.localeCompare(b.customerName, 'ja'))
    .map((c) => ({
      value: c.customerId,
      label: t('page:customerInfo.form.customerOption', {
        name: c.customerName,
        area:
          c.areaCodes.length === 0
            ? t('page:customerInfo.form.customerOptionNoArea')
            : convertAreaLabel(c.areaCodes[0]),
        count: c.pointCount,
      }),
    }))
  // 契約者の選択肢: 契約者名 (需要家 N 件)
  const contractorSelectOptions: DsSelectOptions = [
    ...(options?.contractorOptions ?? []),
  ]
    .sort((a, b) => a.contractorName.localeCompare(b.contractorName, 'ja'))
    .map((c) => ({
      value: c.contractorId,
      label: t('page:customerInfo.form.contractorOption', {
        name: c.contractorName,
        count: c.customerCount,
      }),
    }))
  const areaOptions: DsSelectOptions = AREA_CODE_OPTIONS.map((code) => ({
    value: String(code),
    label: convertAreaLabel(code),
  }))
  const voltageOptions: DsSelectOptions = VOLTAGE_CLASSES.map((v) => ({
    value: v,
    label: t(VOLTAGE_I18N_KEY[v]),
  }))
  const retailerSelectOptions: DsSelectOptions = [
    ...(stage === 'prospect'
      ? [
          {
            value: RETAILER_UNDECIDED,
            label: t('page:customerInfo.form.retailerUndecided'),
          },
        ]
      : []),
    ...retailerOptions.map((r) => ({
      value: r.businessCode,
      label: r.businessName,
    })),
  ]

  const handleSave = (): void => {
    void (async () => {
      setError(undefined)
      // 画面で先に確かめるのは、 server の reason に無い入力の欠けだけ。
      if (start === undefined) {
        setError(t('page:customerInfo.form.supplyStartRequired'))
        return
      }
      // 数値の検査は正規化後 (parseKwInput) で行う: 「,」 だけの入力が「入力なし」として送られるのを防ぐ
      const rejectKw = (reason: SupplyPointErrorReason): void => {
        setError(
          resolveSupplyPointError(new SupplyPointError(reason), t).message,
        )
      }
      if (isInvalidKwInput(kw)) {
        rejectKw('CONTRACT_KW_NOT_POSITIVE')
        return
      }
      if (hasGen && isInvalidKwInput(capacity)) {
        rejectKw('PRIVATE_GENERATION_CAPACITY_REQUIRED')
        return
      }
      if (
        hasGen &&
        hasReverse &&
        (parseKwInput(reverseKw) === undefined || isInvalidKwInput(reverseKw))
      ) {
        rejectKw(
          parseKwInput(reverseKw) === undefined
            ? 'REVERSE_POWER_FLOW_REQUIRED'
            : 'REVERSE_POWER_FLOW_OUT_OF_RANGE',
        )
        return
      }
      const customer: CustomerSelection =
        customerKind === 'existing'
          ? { kind: 'existing', customerId }
          : { kind: 'new', name: customerName }
      const contractor: ContractorSelection =
        contractorKind === 'new'
          ? { kind: 'new', name: newContractorName }
          : { kind: 'existing', contractorId }
      const input: CreateSupplyPointInput = {
        stage,
        customer,
        contractor,
        areaCode,
        retailerBusinessCode: showRetailerSelect
          ? effectiveRetailer
          : undefined,
        voltageClass: voltage,
        supplyPointNumber:
          lockedNumber ?? (number.trim() === '' ? undefined : number.trim()),
        contractKw: parseKwInput(kw),
        supplyStartDate: start,
        supplyEndDate: end,
        privateGeneration: hasGen
          ? {
              has: true,
              capacityKw: parseKwInput(capacity),
              reversePowerFlowContractKw: hasReverse
                ? parseKwInput(reverseKw)
                : undefined,
            }
          : { has: false },
      }
      try {
        await mutateAsync(input)
        showSuccessToast({
          title:
            mode === 'customer'
              ? t('page:customerInfo.toast.customerCreated')
              : stage === 'contracted'
                ? t('page:customerInfo.toast.created')
                : t('page:customerInfo.toast.prospectCreated'),
        })
        onClose()
      } catch (err) {
        setError(resolveSupplyPointError(err, t).message)
      }
    })()
  }

  return (
    <FormModalShell
      errorMessage={error}
      isDirty={isDirty}
      isPending={isPending}
      isSaveDisabled={isContractorNameMissing}
      onClose={onClose}
      onSave={handleSave}
      saveLabel={t('common:action.create')}
      title={
        mode === 'customer'
          ? t('page:customerInfo.modal.createCustomerTitle')
          : t('page:customerInfo.modal.createContractTitle')
      }
    >
      <FormGrid>
        <GridLabel htmlFor="customer-info-stage">
          {t('page:customerInfo.form.stage')}
        </GridLabel>
        <Box
          title={
            lockedNumber === undefined
              ? undefined
              : t('page:customerInfo.form.prospectNeedsNoNumber')
          }
        >
          <AccessibleSegmentedControl
            aria-label={t('page:customerInfo.form.stage')}
            name="customer-info-create-stage"
            onSelect={touch(setStage)}
            options={stageOptions}
            selected={stage}
          />
        </Box>

        <GridLabel htmlFor="customer-info-customer-name">
          {t('page:customerInfo.form.customerName')}
        </GridLabel>
        {customerKind === 'existing' ? (
          <DsSelect
            inputId="customer-info-customer-name"
            onChange={(value) => {
              touch(setCustomerId)(value)
              const area = (options?.customerOptions ?? []).find(
                (c) => c.customerId === value,
              )?.areaCodes[0]
              if (area !== undefined) {
                setAreaCode(area)
                setRetailerCode(undefined)
              }
            }}
            options={customerSelectOptions}
            placeholder={t('page:customerInfo.form.select')}
            value={customerId}
          />
        ) : (
          <Input
            id="customer-info-customer-name"
            onChange={(e) => touch(setCustomerName)(e.target.value)}
            placeholder={t('page:customerInfo.form.customerName')}
            value={customerName}
          />
        )}

        {/* 新しい需要家は契約者も決める: 既存の契約者を選ぶ / 新しく作る (契約者名を入れる。 契約者が 1 件も無い組織は新規が既定) */}
        {customerKind === 'new' && (
          <>
            <GridLabel htmlFor="customer-info-contractor-kind">
              {t('page:customerInfo.form.contractor')}
            </GridLabel>
            <AccessibleSegmentedControl
              aria-label={t('page:customerInfo.form.contractor')}
              name="customer-info-create-contractor-kind"
              onSelect={touch(setContractorKind)}
              options={contractorKindOptions}
              selected={contractorKind}
            />
            {contractorKind === 'new' && (
              <>
                <GridLabel htmlFor="customer-info-new-contractor-name">
                  {t('page:customerInfo.form.contractorName')}
                </GridLabel>
                <Input
                  id="customer-info-new-contractor-name"
                  maxLength={100}
                  onChange={(e) => touch(setNewContractorName)(e.target.value)}
                  placeholder={t('page:customerInfo.form.contractorName')}
                  value={newContractorName}
                />
              </>
            )}
            {contractorKind === 'existing' && (
              <>
                <GridLabel htmlFor="customer-info-contractor-name">
                  {t('page:customerInfo.form.contractorName')}
                </GridLabel>
                <DsSelect
                  inputId="customer-info-contractor-name"
                  onChange={touch(setContractorId)}
                  options={contractorSelectOptions}
                  placeholder={t('page:customerInfo.form.select')}
                  value={contractorId}
                />
              </>
            )}
          </>
        )}

        <GridLabel htmlFor="customer-info-area">
          {t('page:customerInfo.form.area')}
        </GridLabel>
        <DsSelect
          inputId="customer-info-area"
          isDisabled={lockedArea !== undefined}
          onChange={(value) => {
            if (value === undefined) return
            touch(setAreaCode)(Number(value) as AreaCode)
            // 小売の選択肢はエリアに追随する (別エリアの小売が残らないように)
            setRetailerCode(undefined)
          }}
          options={areaOptions}
          value={String(effectiveArea)}
        />

        {showRetailerSelect && (
          <>
            <GridLabel htmlFor="customer-info-retailer">
              {t('page:customerInfo.form.retailer')}
            </GridLabel>
            <DsSelect
              inputId="customer-info-retailer"
              onChange={(value) => touch(setRetailerCode)(value)}
              options={retailerSelectOptions}
              placeholder={t('page:customerInfo.form.select')}
              value={effectiveRetailer ?? RETAILER_UNDECIDED}
            />
          </>
        )}

        <GridLabel htmlFor="customer-info-voltage">
          {t('page:customerInfo.form.voltage')}
        </GridLabel>
        <DsSelect
          inputId="customer-info-voltage"
          onChange={(value) => {
            if (value !== undefined) touch(setVoltage)(value as VoltageClass)
          }}
          options={voltageOptions}
          value={voltage}
        />

        {lockedNumber === undefined ? (
          <>
            <GridLabel htmlFor="customer-info-number">
              {stage === 'prospect'
                ? t('page:customerInfo.form.supplyPointNumberOptional')
                : t('page:customerInfo.form.supplyPointNumber')}
            </GridLabel>
            <Input
              id="customer-info-number"
              inputMode="numeric"
              maxLength={22}
              onChange={(e) => touch(setNumber)(e.target.value)}
              placeholder={t(
                'page:customerInfo.form.supplyPointNumberPlaceholder',
              )}
              value={number}
            />
          </>
        ) : (
          <ReadOnlyRow label={t('page:customerInfo.form.supplyPointNumber')}>
            {lockedNumber}
          </ReadOnlyRow>
        )}

        <GridLabel htmlFor="customer-info-kw">
          {t('page:customerInfo.form.contractKw')}
        </GridLabel>
        <Input
          id="customer-info-kw"
          inputMode="decimal"
          onChange={(e) => touch(setKw)(e.target.value)}
          value={kw}
        />

        <GridLabel htmlFor="customer-info-start">
          {t('page:customerInfo.form.supplyStart')}
        </GridLabel>
        <CompactCivilDateField
          id="customer-info-start"
          onChange={touch(setStart)}
          value={start}
        />

        <GridLabel htmlFor="customer-info-end">
          {t('page:customerInfo.form.supplyEnd')}
        </GridLabel>
        <CompactCivilDateField
          hasClear
          id="customer-info-end"
          onChange={touch(setEnd)}
          value={end}
        />
      </FormGrid>

      <VStack align="stretch" gap={3}>
        <Text textStyle="label">{t('page:customerInfo.selfGen.title')}</Text>
        <Checkbox
          checked={hasGen}
          onCheckedChange={(d: { checked: boolean | 'indeterminate' }) =>
            touch(setHasGen)(d.checked === true)
          }
        >
          {t('page:customerInfo.selfGen.has')}
        </Checkbox>
        <FormGrid>
          <GridLabel htmlFor="customer-info-self-gen-kw">
            {t('page:customerInfo.selfGen.capacityKw')}
          </GridLabel>
          <Input
            disabled={!hasGen}
            id="customer-info-self-gen-kw"
            inputMode="decimal"
            onChange={(e) => touch(setCapacity)(e.target.value)}
            value={capacity}
          />
        </FormGrid>
        <Checkbox
          checked={hasReverse}
          disabled={!hasGen}
          onCheckedChange={(d: { checked: boolean | 'indeterminate' }) =>
            touch(setHasReverse)(d.checked === true)
          }
        >
          {t('page:customerInfo.selfGen.hasReverseFlow')}
        </Checkbox>
        <FormGrid>
          <GridLabel htmlFor="customer-info-reverse-flow-kw">
            {t('page:customerInfo.selfGen.reverseFlowKw')}
          </GridLabel>
          <Input
            disabled={!hasGen || !hasReverse}
            id="customer-info-reverse-flow-kw"
            inputMode="decimal"
            onChange={(e) => touch(setReverseKw)(e.target.value)}
            value={reverseKw}
          />
        </FormGrid>
      </VStack>
    </FormModalShell>
  )
}
